import React, { useState } from 'react';
import { X, Trash2, Mail } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useSettings } from '../hooks/useSettings';
import { api } from '../services/apiClient';

const CartDrawer = () => {
  const { items, removeItem, isOpen, closeCart, clearCart } = useCart();
  const { settings } = useSettings();
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState(null);

  async function handleSubmitOrder(e) {
    e.preventDefault();

    if (!customerName.trim() || !customerEmail.trim()) {
      setMessage({ type: 'error', text: 'Nome e email sono obbligatori' });
      return;
    }

    setIsSubmitting(true);
    setMessage(null);

    try {
      await api.email.sendCart({
        items,
        customerEmail: customerEmail.trim(),
        customerName: customerName.trim(),
        artistEmail: settings.contact_email,
        siteName: settings.site_name,
      });

      setMessage({
        type: 'success',
        text: 'Richiesta inviata con successo! L\'artista ti contatterà a breve.',
      });

      setTimeout(() => {
        clearCart();
        setIsCheckingOut(false);
        setCustomerName('');
        setCustomerEmail('');
        setMessage(null);
        closeCart();
      }, 2000);
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.message || 'Errore durante l\'invio della richiesta',
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <>
      {/* Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-30 backdrop-blur-sm"
          onClick={closeCart}
        />
      )}

      {/* Drawer */}
      <div
        className={`fixed right-0 top-0 h-screen w-full max-w-md bg-white z-40 shadow-xl transform transition-transform duration-300 ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-brand-main">
          <h2 className="font-display text-2xl italic text-brand-dark">Carrello</h2>
          <button
            onClick={closeCart}
            className="p-2 hover:bg-brand-light rounded-full transition-colors"
          >
            <X size={20} className="text-brand-dark" />
          </button>
        </div>

        {/* Items */}
        <div className="flex-1 overflow-y-auto p-6">
          {items.length === 0 ? (
            <p className="text-center text-brand-muted py-8">Il carrello è vuoto</p>
          ) : (
            <div className="space-y-4">
              {items.map((item, index) => (
                <div key={index} className="flex gap-4 pb-4 border-b border-brand-main/30">
                  {/* Thumbnail */}
                  <div className="w-16 h-16 bg-brand-light rounded-lg overflow-hidden flex-shrink-0">
                    <img
                      src={item.image_url || 'https://via.placeholder.com/64'}
                      alt={item.name}
                      className="w-full h-full object-cover"
                    />
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <p className="font-display text-sm italic text-brand-dark truncate">
                      {item.name}
                    </p>
                    <div className="mt-2 space-y-1 text-xs text-brand-muted">
                      {item.categories && (
                        <p><span className="font-medium">Categoria:</span> {item.categories.name}</p>
                      )}
                      {item.formats && (
                        <p><span className="font-medium">Formato:</span> {item.formats.code} — {item.formats.name}</p>
                      )}
                    </div>
                  </div>

                  {/* Remove button */}
                  <button
                    onClick={() => removeItem(index)}
                    className="text-red-500 hover:bg-red-50 p-2 rounded-lg transition-colors"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-brand-main p-6 space-y-4">
          {items.length > 0 && (
            <button
              onClick={() => setIsCheckingOut(true)}
              className="w-full bg-brand-dark text-white py-3 rounded-lg font-medium hover:bg-opacity-90 transition-all flex items-center justify-center gap-2"
            >
              <Mail size={18} />
              Procedi al pagamento
            </button>
          )}
        </div>

        {/* Checkout Modal */}
        {isCheckingOut && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-lg max-w-sm w-full p-6 space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-brand-dark">Invia Richiesta di Acquisto</h3>
                <button
                  onClick={() => {
                    setIsCheckingOut(false);
                    setMessage(null);
                  }}
                  className="p-1 hover:bg-brand-light rounded-full transition-colors"
                >
                  <X size={20} className="text-brand-dark" />
                </button>
              </div>

              {message && (
                <div
                  className={`p-3 rounded-lg text-sm ${
                    message.type === 'success'
                      ? 'bg-green-50 text-green-700 border border-green-200'
                      : 'bg-red-50 text-red-700 border border-red-200'
                  }`}
                >
                  {message.text}
                </div>
              )}

              <form onSubmit={handleSubmitOrder} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">
                    Nome Completo
                  </label>
                  <input
                    type="text"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    placeholder="Il tuo nome"
                    className="w-full p-2 border border-gray-200 rounded-lg outline-none focus:ring-2 focus:ring-brand-main"
                    disabled={isSubmitting}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    value={customerEmail}
                    onChange={(e) => setCustomerEmail(e.target.value)}
                    placeholder="La tua email"
                    className="w-full p-2 border border-gray-200 rounded-lg outline-none focus:ring-2 focus:ring-brand-main"
                    disabled={isSubmitting}
                    required
                  />
                </div>

                <div className="bg-brand-light p-3 rounded-lg text-sm text-gray-700">
                  <p className="font-medium mb-1">Riepilogo richiesta:</p>
                  <p className="text-xs">{items.length} opera{items.length > 1 ? 'e' : ''} nel carrello</p>
                </div>

                <div className="flex gap-2 justify-end pt-2">
                  <button
                    type="button"
                    onClick={() => {
                      setIsCheckingOut(false);
                      setMessage(null);
                    }}
                    disabled={isSubmitting}
                    className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                  >
                    Annulla
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-4 py-2 bg-brand-dark text-white rounded-lg hover:bg-opacity-90 transition-all disabled:opacity-50 flex items-center gap-2"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                        Invio...
                      </>
                    ) : (
                      <>
                        <Mail size={16} />
                        Invia Richiesta
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default CartDrawer;
